import type { ReloadSafety } from './reload-blockers';

export const WEB_BUILD_POLL_INTERVAL_MS = 5 * 60_000;
const RELOAD_TARGET_PREFIX = 'imai:web-build-reloaded:';

export function parseWebBuildIdentity(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || !('buildId' in value))
    return null;
  const id = value.buildId;
  return typeof id === 'string' && /^[a-zA-Z0-9._-]{1,160}$/u.test(id)
    ? id
    : null;
}

/** Inlined by Next at build time in both server and client bundles. */
export const LOADED_WEB_BUILD_ID = parseWebBuildIdentity({
  buildId: process.env.NEXT_PUBLIC_WEB_BUILD_ID,
});

export async function fetchWebBuildIdentity(): Promise<string | null> {
  const response = await fetch('/api/build-identity', {
    cache: 'no-store',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('BUILD_IDENTITY_UNAVAILABLE');
  return parseWebBuildIdentity(await response.json());
}

export function tryReloadForBuild({
  loaded,
  latest,
  safety,
  storage,
  reload,
}: {
  loaded: string | null;
  latest: string | null;
  safety: ReloadSafety;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
}): boolean {
  if (!loaded || !latest || loaded === latest || safety !== 'safe')
    return false;
  try {
    const key = `${RELOAD_TARGET_PREFIX}${latest}`;
    if (storage.getItem(key)) return false;
    storage.setItem(key, '1');
  } catch {
    return false;
  }
  reload();
  return true;
}

export function createWebBuildPoller({
  fetchIdentity,
  onIdentity,
  now = Date.now,
}: {
  fetchIdentity: () => Promise<string | null>;
  onIdentity: (identity: string | null) => void;
  now?: () => number;
}) {
  let pending: Promise<void> | null = null;
  let disposed = false;
  let failures = 0;
  let retryAfter = 0;
  return {
    poll(): Promise<void> {
      if (disposed || now() < retryAfter) return Promise.resolve();
      if (pending) return pending;
      pending = (async () => {
        try {
          const identity = await fetchIdentity();
          if (disposed) return;
          if (!identity) throw new Error('BUILD_IDENTITY_UNKNOWN');
          failures = 0;
          retryAfter = 0;
          onIdentity(identity);
        } catch {
          if (disposed) return;
          failures += 1;
          retryAfter =
            now() +
            Math.min(30_000 * 2 ** (failures - 1), WEB_BUILD_POLL_INTERVAL_MS);
          onIdentity(null);
        } finally {
          pending = null;
        }
      })();
      return pending;
    },
    dispose() {
      disposed = true;
    },
  };
}
